/**
 * The one-line note shown under a user's name on the users screen when they are
 * an ADMIN of the department being listed.
 *
 * Grant provenance (`grantedBy` / `grantedAt` on the role) only started being
 * recorded recently, so most existing admin roles carry none. Every branch here
 * degrades to a bare "אדמין" rather than inventing a granter or a date.
 */

type Id = string | { _id?: string } | null | undefined;

interface Role {
  role?: string;
  department?: Id;
  grantedBy?: { name?: string } | null;
  grantedAt?: string | Date | null;
}

interface UserLike {
  _id?: string;
  roles?: Role[] | null;
}

interface DepartmentLike {
  _id?: string;
  createdBy?: Id;
  createdAt?: string | Date | null;
}

const idOf = (value: Id): string | undefined => {
  if (!value) return undefined;
  return typeof value === 'string' ? value : value._id;
};

export const formatGrantDate = (date: string | Date | null | undefined): string | null => {
  // `new Date(null)` is the 1970 epoch, not an invalid date - reject empty values first.
  if (!date) return null;
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(parsed.getDate())}.${pad(parsed.getMonth() + 1)}.${parsed.getFullYear()}`;
};

export const getAdminNote = (user: UserLike, department: DepartmentLike): string | null => {
  const roleInDepartment = user?.roles?.find(r => idOf(r.department) === department?._id);
  if (roleInDepartment?.role !== 'ADMIN') return null;

  const creatorId = idOf(department.createdBy);
  if (creatorId && creatorId === user._id) {
    const createdOn = formatGrantDate(department.createdAt);
    return createdOn ? `אדמין, יצר את המחלקה ב ${createdOn}` : 'אדמין';
  }

  const granterName = roleInDepartment.grantedBy?.name;
  const grantedOn = formatGrantDate(roleInDepartment.grantedAt);
  if (granterName && grantedOn) {
    return `אדמין, ניתן ע"י ${granterName} ב ${grantedOn}`;
  }
  return 'אדמין';
};
